import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/StateRequirementInfo.css';
import { formatCalendarDate } from '../utils/timezone';

interface Category {
  _id: string;
  categoryTag: string;
  name: string;
  description?: string;
  minCredits?: number;
  maxCredits?: number;
}

interface StateRequirement {
  _id: string;
  professionID: string;
  stateID: number;
  effectiveDate: string;
  expiryDate?: string;
  totalCreditsRequired: number;
  cycleLengthYears: number;
  categoryIDs: Category[];
  hasFixedCycleDates?: boolean;
  fixedCycleStartMonth?: number;
  fixedCycleStartDay?: number;
  fixedCycleEndMonth?: number;
  fixedCycleEndDay?: number;
  cycleDateNotes?: string;
  allowCarryover?: boolean;
  carryoverMaxCredits?: number;
  selfStudyMaxPercent?: number;
  lectureMinPercent?: number;
  notes?: string;
  stateprofessionlink1?: string;
  stateprofessionlink1desc?: string;
  stateprofessionlink2?: string;
  stateprofessionlink2desc?: string;
  professionName?: string;
  stateName?: string;
  creditTerminology?: string;
}

const StateRequirementInfo: React.FC = () => {
  const { professionID, stateID } = useParams<{ professionID: string; stateID: string }>();
  const navigate = useNavigate();
  const [requirement, setRequirement] = useState<StateRequirement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequirement = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/state-requirements/${professionID}/${stateID}`
        );

        if (!response.ok) {
          throw new Error('Requirements not found for this profession and state');
        }

        const data = await response.json();
        setRequirement(data);
      } catch (err: any) {
        console.error('Error fetching requirement:', err);
        setError(err.message || 'Failed to load requirements');
      } finally {
        setLoading(false);
      }
    };

    if (professionID && stateID) {
      fetchRequirement();
    }
  }, [professionID, stateID]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return formatCalendarDate(dateString);
  };

  const formatCycleDate = (month?: number, day?: number) => {
    if (!month || !day) return '';
    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[month]} ${day}`;
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !requirement) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error || 'Requirements not found'}
        </div>
        <button className="btn btn-primary" onClick={handleBack}>
          ← Go Back
        </button>
      </div>
    );
  }

  // Calculate format requirements
  const totalCredits = requirement.totalCreditsRequired;
  const creditTerm = requirement.creditTerminology || 'credits';
  const liveMinPercent = requirement.lectureMinPercent;
  const selfStudyMaxPercent = requirement.selfStudyMaxPercent;

  // Separate mandatory and optional categories
  const mandatoryCategories = requirement.categoryIDs.filter(cat => 
    cat.minCredits && cat.minCredits > 0
  );
  const optionalCategories = requirement.categoryIDs.filter(cat => 
    !cat.minCredits || cat.minCredits === 0
  );

  return (
    <div className="container my-5 requirement-page">
      {/* Header with Actions */}
      <div className="d-flex justify-content-between align-items-center mb-4 no-print">
        <button className="btn btn-outline-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button className="btn btn-outline-primary" onClick={handlePrint}>
          ⎙ Print
        </button>
      </div>

      {/* Main Title Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-body text-center py-5">
          <h1 className="display-4 text-primary fw-bold mb-3">
            {requirement.stateName?.toUpperCase() || `State ${stateID}`}
          </h1>
          <h2 className="h3 text-secondary mb-3">
            {requirement.professionName || professionID}
          </h2>
          {(requirement.stateprofessionlink1 || requirement.stateprofessionlink2) && (
            <div className="mb-3">
              {requirement.stateprofessionlink1 && (
                <a
                  href={requirement.stateprofessionlink1}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-sm"
                >
                  ◈ Visit Official State Board
                </a>
              )}
            </div>
          )}
          <h3 className="h4 text-muted fw-light mb-4">
            Continuing Education Requirements
          </h3>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <span className="badge bg-primary fs-6">
              Effective: {formatDate(requirement.effectiveDate)}
            </span>
            <span className="badge bg-secondary fs-6">
              Last Updated: {new Date().toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Cycle Overview Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6">
          <div className="card h-100 bg-primary text-white shadow">
            <div className="card-body text-center py-4">
              <div className="display-3 fw-bold mb-2">{totalCredits}</div>
              <h5 className="mb-0">{creditTerm} Required</h5>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="card h-100 bg-success text-white shadow">
            <div className="card-body text-center py-4">
              <div className="display-3 fw-bold mb-2">{requirement.cycleLengthYears}</div>
              <h5 className="mb-0">
                Year{requirement.cycleLengthYears !== 1 ? 's' : ''} per Cycle
              </h5>
            </div>
          </div>
        </div>
      </div>

      {/* Cycle Date Notes */}
      {requirement.cycleDateNotes && (
        <div className="alert alert-info" role="alert">
          <strong>◉ Cycle Information:</strong> {requirement.cycleDateNotes}
        </div>
      )}

      {/* Format Requirements */}
      {(liveMinPercent || selfStudyMaxPercent) && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h4 className="card-title mb-4">
              ◈ Format Requirements
            </h4>
            <hr />
            <div className="row g-3">
              {liveMinPercent && (
                <div className="col-12 col-md-6">
                  <div className="p-3 bg-success bg-opacity-10 border border-success rounded">
                    <h5 className="mb-3">Live/Interactive Minimum</h5>
                    <div className="display-5 fw-bold text-success">
                      {Math.round((totalCredits * liveMinPercent) / 100)} {creditTerm}
                    </div>
                    <small className="text-muted">({liveMinPercent}% of total)</small>
                  </div>
                </div>
              )}
              {selfStudyMaxPercent && (
                <div className="col-12 col-md-6">
                  <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded">
                    <h5 className="mb-3">Self-Study Maximum</h5>
                    <div className="display-5 fw-bold text-warning">
                      {Math.round((totalCredits * selfStudyMaxPercent) / 100)} {creditTerm}
                    </div>
                    <small className="text-muted">({selfStudyMaxPercent}% of total)</small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Categories */}
      {mandatoryCategories.length > 0 && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h4 className="card-title text-danger mb-4">◈ Mandatory Categories</h4>
            <hr />
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="text-center">Min {creditTerm}</th>
                    <th className="text-center">Max {creditTerm}</th>
                  </tr>
                </thead>
                <tbody>
                  {mandatoryCategories.map((cat) => (
                    <tr key={cat._id}>
                      <td className="fw-bold">{cat.name}</td>
                      <td className="text-muted">{cat.description || '-'}</td>
                      <td className="text-center">
                        <span className="badge bg-danger fs-6">
                          {cat.minCredits || '-'}
                        </span>
                      </td>
                      <td className="text-center">
                        {cat.maxCredits ? (
                          <span className="badge bg-warning fs-6">{cat.maxCredits}</span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Optional Categories */}
      {optionalCategories.length > 0 && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h4 className="card-title text-primary mb-4">◈ Additional Categories</h4>
            <hr />
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="text-center">Max {creditTerm}</th>
                  </tr>
                </thead>
                <tbody>
                  {optionalCategories.map((cat) => (
                    <tr key={cat._id}>
                      <td className="fw-bold">{cat.name}</td>
                      <td className="text-muted">{cat.description || '-'}</td>
                      <td className="text-center">
                        {cat.maxCredits ? (
                          <span className="badge bg-info fs-6">{cat.maxCredits}</span>
                        ) : <span className="text-muted">No Limit</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Additional Rules */}
      {(requirement.allowCarryover || requirement.notes) && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h4 className="card-title mb-4">◈ Important Notes & Special Rules</h4>
            <hr />
            
            {requirement.allowCarryover && (
              <div className="alert alert-info" role="alert">
                <strong>Carryover Allowed:</strong> {requirement.carryoverMaxCredits 
                  ? `Up to ${requirement.carryoverMaxCredits} ${creditTerm} may be carried forward to the next cycle.`
                  : 'Excess credits may be carried forward (check with state board for limits).'
                }
              </div>
            )}

            {requirement.notes && (
              <div className="p-3 bg-light border rounded">
                <pre className="mb-0" style={{ whiteSpace: 'pre-line', fontFamily: 'inherit' }}>
                  {requirement.notes}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Official Resources */}
      {(requirement.stateprofessionlink1 || requirement.stateprofessionlink2) && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h4 className="card-title mb-4">◈ Official Resources</h4>
            <hr />
            <div className="d-flex flex-column gap-3">
              {requirement.stateprofessionlink1 && (
                <div>
                  <a
                    href={requirement.stateprofessionlink1}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                  >
                    {requirement.stateprofessionlink1desc || 'Official State Board Website'}
                    <span>↗</span>
                  </a>
                </div>
              )}
              {requirement.stateprofessionlink2 && (
                <div>
                  <a
                    href={requirement.stateprofessionlink2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                  >
                    {requirement.stateprofessionlink2desc || 'Additional Resource'}
                    <span>↗</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="alert alert-warning" role="alert">
        <strong>◉ Disclaimer:</strong> This information is provided as a guide based on official state board requirements 
        as of {new Date().toLocaleDateString()}. Requirements may change. Always verify current requirements with your 
        state licensing board before renewing your license. CECtrack is not responsible for any inaccuracies or changes 
        in state requirements.
      </div>

      {/* Help Section */}
      <div className="card bg-info bg-opacity-10 border-info">
        <div className="card-body">
          <h5 className="card-title">◉ Need Help?</h5>
          <ul className="mb-0">
            <li>Contact your state licensing board for official guidance</li>
            <li>Questions about this page? Contact CECtrack Support</li>
            <li>Report outdated information using the contact form</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StateRequirementInfo;
